import { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Settings, Lock, LogOut, Globe, Loader2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, errText, useAuth } from "@/lib/auth";
import { APPS, BUNDLE, PRICE, ownsNone } from "@/lib/apps";
import { useAppHead } from "@/components/app/useAppHead";
import { Keypad, LockScreen, clearLock, lockEnabled, setLock, useAppLock } from "@/components/app/AppLock";
import InstallGuide from "@/components/app/InstallGuide";
import { PARTNER_PRICE, UPGRADE_PRICE, hasPartnerPrice, isUpgrade, usePartnerOffer } from "@/lib/partnerInvite";
import PartnerInviteCard from "@/components/app/PartnerInviteCard";
import FigurePrefs from "@/components/app/FigurePrefs";
import { CalmToggle } from "@/lib/calm";
import PushToggle from "@/components/app/PushToggle";

const AppCtx = createContext(APPS.gates);
export const useApp = () => useContext(AppCtx);

const btn = "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-tt-mono text-[12px] uppercase tracking-[0.2em] transition-colors disabled:opacity-60";
export const gold = `${btn} bg-[#D9B47A] text-[#0A0706] hover:bg-[#F2E8DF]`;
export const ghost = `${btn} border border-[#D9B47A]/40 text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706]`;

export const AppIcon = ({ app, className = "w-14 h-14" }) => <img src={app.icon} alt="" className={`${className} rounded-[22%] shadow-[0_0_30px_rgba(217,180,122,0.12)]`} />;

const Centered = ({ children, testId }) => (
  <main data-testid={testId} className="min-h-[100dvh] flex items-center justify-center px-6 py-16 pt-[max(4rem,env(safe-area-inset-top))]">
    <div className="w-full max-w-sm">{children}</div>
  </main>
);

const Wordmark = () => {
  const app = useApp();
  return (
    <div data-testid="app-wordmark" className="flex items-center gap-3">
      <AppIcon app={app} className="w-7 h-7" />
      <span className="font-tt-mono text-[11px] uppercase tracking-[0.3em] text-[#D9B47A]">{app.name}</span>
      <Link to={`${app.base}/settings`} data-testid="app-settings-link" aria-label="Settings" className="ml-auto p-2 -mr-2 text-[#A8998C] hover:text-[#D9B47A] transition-colors"><Settings className="w-5 h-5" strokeWidth={1.5} /></Link>
    </div>
  );
};

export const Page = ({ testId, eyebrow, title, children }) => (
  <main data-testid={testId} className="max-w-xl mx-auto px-5 pt-[max(2.5rem,calc(env(safe-area-inset-top)+1.5rem))] pb-32">
    <Wordmark />
    {eyebrow && <p className="mt-10 font-tt-mono text-[11px] uppercase tracking-[0.3em] text-[#A8998C]">{eyebrow}</p>}
    {title && <h1 className="mt-4 font-display text-4xl leading-tight">{title}</h1>}
    {children}
  </main>
);

const Splash = () => {
  const app = useApp();
  return (
    <Centered testId="app-splash">
      <div className="flex flex-col items-center">
        <AppIcon app={app} className="w-20 h-20" />
        <p className="mt-6 font-display text-2xl">{app.name}</p>
        <Loader2 className="mt-8 w-5 h-5 animate-spin text-[#D9B47A]" />
      </div>
    </Centered>
  );
};

const AppSignIn = () => {
  const app = useApp();
  const { refresh } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/login", form);
      await refresh();
    } catch (err) {
      setError(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const field = "w-full bg-transparent border-b border-[#D9B47A]/30 py-3 text-lg placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors";
  const [first, ...rest] = app.name.split(" ");
  return (
    <Centered testId="app-signin">
      <AppIcon app={app} className="w-16 h-16" />
      <h1 data-testid="app-signin-title" className="mt-8 font-display text-4xl leading-tight">{first} <em className="text-[#D9B47A]">{rest.join(" ")}</em></h1>
      <p className="mt-2 text-[#A8998C]">Sign in to enter.</p>
      <form onSubmit={submit} className="mt-10 space-y-6">
        <input data-testid="app-signin-email" type="email" autoComplete="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
        <input data-testid="app-signin-password" type="password" autoComplete="current-password" required placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} />
        {error && <p data-testid="app-signin-error" className="text-sm text-[#E8838E]">{error}</p>}
        <button data-testid="app-signin-submit" disabled={busy} className={`${gold} w-full`}>{busy && <Loader2 className="w-4 h-4 animate-spin" />} Enter</button>
      </form>
      <div className="mt-8 flex flex-col gap-3 text-sm text-[#A8998C]">
        <a href="/signin" data-testid="app-signin-forgot" className="underline underline-offset-4 hover:text-[#F2E8DF]">Forgotten your password?</a>
        <a href={`/initiate?product=${app.key}`} data-testid="app-signin-initiate" className="underline underline-offset-4 hover:text-[#F2E8DF]">New here? Unlock {app.name} · {PRICE}</a>
      </div>
    </Centered>
  );
};

const AppPaywall = () => {
  const app = useApp();
  const { user, refresh, logout } = useAuth();
  const [busy, setBusy] = useState("");
  const offer = usePartnerOffer();
  const partner = hasPartnerPrice(user, offer);
  const pay = async (product = app.key) => {
    setBusy(product === app.key ? "pay" : "bundle");
    try {
      const { data } = await api.post("/initiates/checkout", { origin_url: window.location.origin, product, partner_code: offer?.code ?? "" });
      if (data?.checkout_url) return window.location.assign(data.checkout_url);
      if (data?.already) await refresh();
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    }
    return setBusy("");
  };
  const check = async () => {
    setBusy("check");
    const u = await refresh();
    setBusy("");
    if (!app.owns(u)) toast("Your unlock isn’t showing yet. If you’ve just paid, give it a minute and try again.");
  };
  return (
    <Centered testId="app-paywall">
      <AppIcon app={app} className="w-16 h-16" />
      <p className="mt-8 font-tt-mono text-[11px] uppercase tracking-[0.3em] text-[#D9B47A]">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</p>
      <h1 className="mt-4 font-display text-4xl leading-tight"><em className="text-[#D9B47A]">{app.name}</em> opens with a single offering.</h1>
      <ul className="mt-8 space-y-3 text-[15px] text-[#CFC2B6]">
        {[...app.includes, "A private, passcode-locked space on your phone"].map((t) => (
          <li key={t} className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C2414F] shrink-0" />{t}</li>
        ))}
      </ul>
      <button data-testid="app-paywall-pay" onClick={() => pay()} disabled={!!busy} className={`${gold} w-full mt-10`}>{busy === "pay" && <Loader2 className="w-4 h-4 animate-spin" />} Unlock for life · {isUpgrade(user, app.key) ? `${UPGRADE_PRICE} AUD · your upgrade price` : partner ? `${PARTNER_PRICE[app.key]} AUD · partner price` : PRICE}</button>
      {ownsNone(user) && (
        <button data-testid="app-paywall-bundle" onClick={() => pay("bundle")} disabled={!!busy} className="mt-3 w-full border border-[#E8838E]/50 bg-[#1A0B10] px-5 py-4 text-left hover:border-[#E8838E] transition-colors disabled:opacity-60">
          <span className="flex items-center justify-between gap-3">
            <span className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#E8838E]">Both apps · save {BUNDLE.save}</span>
            {busy === "bundle" && <Loader2 className="w-4 h-4 animate-spin text-[#E8838E]" />}
          </span>
          <span className="mt-1 block font-display text-xl">Unlock both for {partner ? `${PARTNER_PRICE.bundle} AUD` : BUNDLE.label}</span>
        </button>
      )}
      <button data-testid="app-paywall-refresh" onClick={check} disabled={!!busy} className={`${ghost} w-full mt-3`}>{busy === "check" && <Loader2 className="w-4 h-4 animate-spin" />} I’ve already paid</button>
      <p className="mt-4 text-[13px] text-[#A8998C]">One payment, no subscription. Signed in as {user?.email}.</p>
      <button data-testid="app-paywall-signout" onClick={logout} className="mt-6 w-full text-sm text-[#A8998C] underline underline-offset-4">Sign out</button>
    </Centered>
  );
};

const TabBar = ({ tabs }) => {
  const app = useApp();
  return (
    <nav data-testid="app-tabbar" style={{ backgroundColor: `${app.bg}E6` }} className="fixed bottom-0 inset-x-0 z-50 border-t border-[#D9B47A]/15 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
      <div className="max-w-xl mx-auto grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
        {tabs.map(([path, label, Icon]) => (
          <NavLink key={label} to={`${app.base}${path ? `/${path}` : ""}`} end={!path} data-testid={`app-tab-${label.toLowerCase()}`} className={({ isActive }) => `flex flex-col items-center gap-1 py-3 text-[10px] font-tt-mono uppercase tracking-[0.15em] transition-colors ${isActive ? "text-[#D9B47A]" : "text-[#8F8176] hover:text-[#CFC2B6]"}`}>
            <Icon className="w-5 h-5" strokeWidth={1.5} />
            {label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

const PinSetup = ({ uid, onDone }) => {
  const app = useApp();
  const [first, setFirst] = useState("");
  const [error, setError] = useState("");
  const complete = async (pin) => {
    if (!first) {
      setFirst(pin);
      setError("");
      return true;
    }
    if (pin !== first) {
      setFirst("");
      setError("Those didn’t match. Let’s begin again.");
      return false;
    }
    await setLock(uid, pin);
    onDone(true);
    return true;
  };
  return (
    <div data-testid="app-pin-setup" className="fixed inset-0 z-[200] flex items-center justify-center px-6" style={{ backgroundColor: app.bg }}>
      <Keypad key={first ? "confirm" : "choose"} title={first ? "Confirm passcode" : "Choose a passcode"} sub={first ? "Enter the same four digits again." : "Four digits you’ll enter each time your apps open. It works for both apps on this device."} error={error} onComplete={complete}
        footer={<button data-testid="app-pin-cancel" onClick={() => onDone(false)} className="mt-8 text-sm text-[#A8998C] underline underline-offset-4">Cancel</button>} />
    </div>
  );
};

const OtherApp = () => {
  const app = useApp();
  const { user } = useAuth();
  const other = app.key === "gates" ? APPS.quantum : APPS.gates;
  return (
    <a href={other.base} data-testid="app-other-app" className="mt-6 flex items-center gap-4 border border-[#D9B47A]/20 p-5 hover:border-[#D9B47A]/60 transition-colors">
      <AppIcon app={other} className="w-12 h-12 shrink-0" />
      <span className="min-w-0">
        <span className="block font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">{other.owns(user) ? "Also yours" : `Also from Goddess Ariel · ${PRICE}`}</span>
        <span className="block mt-1 font-display text-xl">{other.name}</span>
      </span>
      <ArrowUpRight className="ml-auto w-5 h-5 text-[#D9B47A] shrink-0" />
    </a>
  );
};

const AppSettings = () => {
  const app = useApp();
  const { user, logout } = useAuth();
  const [on, setOn] = useState(() => lockEnabled(user.id));
  const [setup, setSetup] = useState(false);
  const toggle = () => {
    if (on) {
      clearLock(user.id);
      setOn(false);
      toast.success("Passcode lock is off.");
    } else setSetup(true);
  };
  return (
    <Page testId="app-settings" eyebrow="Settings" title={app.name}>
      <section className="mt-10 border border-[#D9B47A]/20 p-6">
        <div className="flex items-start justify-between gap-5">
          <div>
            <p className="font-display text-xl flex items-center gap-2"><Lock className="w-4 h-4 text-[#D9B47A]" /> Passcode lock</p>
            <p className="mt-1 text-sm text-[#A8998C]">Ask for a 4-digit passcode whenever the app opens on this device, and after 30 seconds away.</p>
          </div>
          <button data-testid="app-lock-switch" role="switch" aria-checked={on} onClick={toggle} className={`relative shrink-0 w-14 h-8 rounded-full border transition-colors duration-300 ${on ? "bg-[#C2414F] border-[#C2414F]" : "border-[#D9B47A]/40"}`}>
            <span className={`absolute top-1 left-1 w-[22px] h-[22px] rounded-full transition-transform duration-300 ${on ? "translate-x-6 bg-[#F2E8DF]" : "bg-[#D9B47A]/60"}`} />
          </button>
        </div>
        {on && <button data-testid="app-lock-change" onClick={() => setSetup(true)} className="mt-4 text-sm text-[#D9B47A] underline underline-offset-4">Change passcode</button>}
      </section>
      <Link to={`${app.base}/us`} data-testid="settings-our-field" className="mt-6 flex items-center justify-between border border-[#E8838E]/30 p-5 hover:border-[#E8838E]/70 transition-colors"><span><span className="block font-display text-xl">Our Field</span><span className="block text-sm text-[#A8998C]">Your shared streak, love notes, maps and scheduled practices</span></span><span className="text-[#E8838E]">→</span></Link>
      <PartnerInviteCard />
      <CalmToggle />
      <FigurePrefs />
      <PushToggle />
      <div className="mt-6"><InstallGuide app={app} /></div>
      <OtherApp />
      <section className="mt-6 border border-[#D9B47A]/20 divide-y divide-[#D9B47A]/15">
        <p className="p-5 text-sm text-[#A8998C]">Signed in as <span data-testid="app-settings-email" className="text-[#F2E8DF]">{user.email}</span></p>
        <a href="/" data-testid="app-open-website" className="flex items-center gap-3 p-5 text-[#CFC2B6] hover:text-[#F2E8DF]"><Globe className="w-4 h-4 text-[#D9B47A]" /> Open The Tantric Temple website</a>
        <button data-testid="app-signout" onClick={logout} className="w-full flex items-center gap-3 p-5 text-left text-[#CFC2B6] hover:text-[#E8838E]"><LogOut className="w-4 h-4 text-[#D9B47A]" /> Sign out</button>
      </section>
      {setup && <PinSetup uid={user.id} onDone={(ok) => { setSetup(false); if (ok) { setOn(true); toast.success("Passcode lock is on."); } }} />}
    </Page>
  );
};

const Unlocked = ({ tabs, children }) => {
  const app = useApp();
  const { user, logout } = useAuth();
  const [locked, setLocked] = useAppLock(user.id);
  const { pathname } = useLocation();
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    if (locked) return;
    const d = new Date();
    const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const key = `tt-visit-${app.key}-${user.id}`;
    if (localStorage.getItem(key) === day) return;
    api.post("/usage/visit", { app: app.key, day }).then(() => localStorage.setItem(key, day)).catch(() => {});
  }, [locked, app.key, user.id]);
  if (locked) {
    return <LockScreen uid={user.id} title={app.name} onUnlock={() => setLocked(false)} onForgot={() => { clearLock(user.id); setLocked(false); logout(); }} />;
  }
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} data-testid={`${app.key}-app`}>
      <Routes>
        {children}
        <Route path="settings" element={<AppSettings />} />
      </Routes>
      <TabBar tabs={tabs} />
    </motion.div>
  );
};

// Shared frame for both installable apps: head tags, sign-in, the app's own paywall, passcode lock and tab bar.
export const AppFrame = ({ app, tabs, children }) => {
  useAppHead(app);
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user === false && window.location.pathname !== app.base) navigate(app.base, { replace: true });
  }, [user, navigate, app.base]);
  let body;
  if (user === null) body = <Splash />;
  else if (!user) body = <AppSignIn />;
  else if (!app.owns(user) && user.role !== "admin") body = <AppPaywall />;
  else body = <Unlocked tabs={tabs}>{children}</Unlocked>;
  return (
    <AppCtx.Provider value={app}>
      <div className="min-h-[100dvh]" style={{ backgroundColor: app.bg }}>{body}</div>
    </AppCtx.Provider>
  );
};
